import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { FilePreviewActions } from "@states/file-preview/file-preview.actions";
import { catchError, mergeMap, Observable, share, switchMap, tap } from 'rxjs';
import { ReportsService } from "src/app/services/reports.service";
import * as Papa from 'papaparse';
import { SharedActions } from "@states/shared/shared.action-types";
import { UtilsService } from "src/app/edge/utils.service";
import { TranslateService } from "@ngx-translate/core";
import { downloadFile } from "@ui";
import { FilePreviewModel } from '@models/file-preview.model';

@Injectable()
export class FilePreviewEffect {
    private readonly actions$ = inject(Actions);
    private readonly reportsService = inject(ReportsService);
    private readonly utilService = inject(UtilsService);
    private readonly translateService = inject(TranslateService);

    public downloadReportArtifactById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilePreviewActions.downloadReportArtifactById),
            switchMap(({ reportId, fallbackFileName }) => {
                return this.reportsService.loadArtifactFile(reportId)
                    .pipe(
                        mergeMap((response) => {
                            if (!response.body) {
                                return [
                                    SharedActions.showMessage({ error: 'File download failed' }),
                                ];
                            }

                            const url = window.URL.createObjectURL(response.body);
                            const contentDisposition = response.headers.get('content-disposition');

                            downloadFile(url, contentDisposition, fallbackFileName)

                            return [FilePreviewActions.downloadReportArtifactByIdSuccess()];
                        }),
                        catchError((error) => [
                            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                        ]),
                    )
            }),
            share(),
        ),
    );

    public beforeLoadReportArtifactCsv$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilePreviewActions.beforeLoadReportArtifactCsv),
            switchMap(({ reportId }) => [
                FilePreviewActions.setLoading({ isLoading: true }),
                FilePreviewActions.loadReportArtifactCsv({ reportId }),
            ]),
        ),
    );

    public loadReportArtifactCsv$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilePreviewActions.loadReportArtifactCsv),
            switchMap(({ reportId }) =>
                this.reportsService.loadArtifactFile(reportId).pipe(
                    switchMap((response) => {
                        const blob = response.body;

                        if (!blob) {
                            return [
                                SharedActions.showMessage({ error: this.translateService.instant('FILE_PREVIEW.LOAD_FAILED') }),
                                FilePreviewActions.setLoading({ isLoading: false }),
                            ];
                        }

                        const url = window.URL.createObjectURL(response.body);
                        const contentDisposition = response.headers.get('content-disposition');

                        return new Observable<any>((observer) => {
                            // @ts-ignore
                            Papa.parse(blob, {
                                header: true,
                                skipEmptyLines: true,
                                complete: (result: FilePreviewModel.CsvParseResult) => {
                                    observer.next(
                                        FilePreviewActions.setCsvParseResult({
                                            csvParseResult: result
                                        })
                                    );
                                    observer.next(
                                        FilePreviewActions.setContentDisposition({
                                            contentDisposition: contentDisposition,
                                        })
                                    );
                                    observer.next(
                                        FilePreviewActions.setFileUrl({
                                            fileUrl: url
                                        })
                                    );
                                    observer.next(
                                        FilePreviewActions.setLoading({ isLoading: false })
                                    );
                                    observer.complete();
                                },
                                error: () => {
                                    observer.next(
                                        FilePreviewActions.setLoading({ isLoading: false })
                                    );
                                    observer.complete();
                                }
                            });
                        });
                    }),
                    catchError((error) => [
                        SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                        FilePreviewActions.setLoading({ isLoading: false }),
                    ])
                )
            )
        )
    );

    public beforeGetReportFileUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilePreviewActions.beforeGetReportFileUrl),
            switchMap(({ reportId }) => [
                FilePreviewActions.setLoading({ isLoading: true }),
                FilePreviewActions.getReportFileUrl({ reportId }),
            ]),
        ),
    );

    public getReportFileUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilePreviewActions.getReportFileUrl),
            switchMap(({ reportId }) => {
                return this.reportsService.loadArtifactFile(reportId)
                    .pipe(
                        switchMap((response) => {
                            const blob = response.body;

                            if (!blob) {
                                return [
                                    this.translateService.instant('FILE_PREVIEW.LOAD_FAILED'),
                                    FilePreviewActions.setLoading({ isLoading: false }),
                                ];
                            }

                            const url = URL.createObjectURL(blob);
                            const contentDisposition = response.headers.get('content-disposition');

                            return [
                                FilePreviewActions.setLoading({ isLoading: false }),
                                FilePreviewActions.setFileUrl({ fileUrl: url }),
                                FilePreviewActions.setContentDisposition({ contentDisposition: contentDisposition }),
                            ];
                        }),
                        catchError((error) => [
                            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
                            FilePreviewActions.setLoading({ isLoading: false }),
                        ]),
                    );
            }),
            share(),
        ),
    );

    public getReportArtifactFileDownloadLink$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(FilePreviewActions.getReportArtifactFileDownloadLink),
                switchMap(({ reportId }) =>
                    this.reportsService.downloadReportArtifact(reportId).pipe(
                        tap((artifact) => {
                            window.open(artifact.url, '_blank');
                        })
                    )
                )
            ),
        { dispatch: false }
    );
}
